import { z } from 'zod';
import { TimestampSchema } from './common';
import {
  ConditionSchema,
  CURRENCIES,
  DestinationSchema,
  DimensionUnitSchema,
  PrioritySchema,
  ProgressStatusSchema,
} from './enums';

/**
 * One image attached to an item. Stored as an array on the item (PRD §8.4 —
 * "leave room for multiple images even if V1 shows one primary"). The Storage
 * Cloud Function fills `thumbPath`/`displayPath` after re-compression. Public
 * download URLs are never persisted; the owner's browser resolves these paths
 * through the authenticated Storage SDK and keeps temporary blob URLs in
 * memory only.
 */
export const ItemImageSchema = z.object({
  /** Storage path of the uploaded original (source of truth). */
  storagePath: z.string().min(1),
  /** Storage path of the re-compressed display image. */
  displayPath: z.string().min(1).optional(),
  /** Storage path of the generated thumbnail. */
  thumbPath: z.string().min(1).optional(),
  /** Cache revision bumped whenever a derivative is regenerated. */
  revision: z.number().nonnegative().optional(),
  /** Ephemeral, in-memory blob URL. Never written to Firestore. */
  url: z.string().url().optional(),
  /** Ephemeral thumbnail blob URL. Never written to Firestore. */
  thumbUrl: z.string().url().optional(),
  isPrimary: z.boolean().default(false),
  createdAt: TimestampSchema,
});
export type ItemImage = z.infer<typeof ItemImageSchema>;

export const ItemSchema = z.object({
  id: z.string().min(1),

  name: z.string().trim().min(1).max(140),
  /** Optional friendly label to identify the item; falls back to `name`. */
  nickname: z.string().trim().max(140).optional(),

  images: z.array(ItemImageSchema).default([]),
  /** Ephemeral owner-only blob URLs populated after authenticated downloads. */
  primaryImageUrl: z.string().url().optional(),
  primaryThumbUrl: z.string().url().optional(),

  /** Enum key (DefaultRoomKey) or a user-typed custom room string. */
  room: z.string().trim().min(1).optional(),
  /** Enum key (DefaultCategoryKey) or a user-typed custom category string. */
  category: z.string().trim().min(1).optional(),

  /**
   * Furniture-type key (see apps/web furniture registry) used as the item's
   * visual when it has no photo. Optional — falls back to a neutral placeholder.
   */
  iconType: z.string().trim().min(1).optional(),

  destination: DestinationSchema.default('undecided'),
  progressStatus: ProgressStatusSchema.default('not_started'),

  // Dimensions — structured (preferred) + free-text fallback (PRD §8.9).
  length: z.number().nonnegative().optional(),
  width: z.number().nonnegative().optional(),
  height: z.number().nonnegative().optional(),
  dimensionUnit: DimensionUnitSchema.optional(),
  dimensionsText: z.string().trim().max(120).optional(),

  /** Weight in kilograms (PRD §8.9). Optional. */
  weight: z.number().nonnegative().optional(),

  condition: ConditionSchema.optional(),
  quantity: z.number().int().positive().default(1),
  estimatedValue: z.number().nonnegative().optional(),
  currency: z.enum(CURRENCIES).optional(),

  boxId: z.string().optional(),
  storageLocation: z.string().trim().max(120).optional(),

  fragile: z.boolean().default(false),
  priority: PrioritySchema.optional(),

  /** Comments/notes (PRD §8.5). */
  notes: z.string().trim().max(2000).optional(),

  // --- Reserved for later (PRD §20 "leave room for"). Not surfaced in V1. ---
  privateNotes: z.string().optional(),
  publicNotes: z.string().optional(),
  includeInMoverQuote: z.boolean().optional(),

  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Item = z.infer<typeof ItemSchema>;

/** Fields a user can set/edit through the Add/Edit Item form. */
export const ItemDraftSchema = ItemSchema.omit({
  id: true,
  images: true,
  primaryImageUrl: true,
  primaryThumbUrl: true,
  createdAt: true,
  updatedAt: true,
}).partial({
  destination: true,
  progressStatus: true,
  quantity: true,
  fragile: true,
});
export type ItemDraft = z.infer<typeof ItemDraftSchema>;
